import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { UserPlus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeamJoinRequestCard = () => {
  const [searchData, setSearchData] = useState({ club_name: '', team_name: '' });
  const [searchResults, setSearchResults] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [joinMessage, setJoinMessage] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(null);

  useEffect(() => {
    loadMyRequests();
  }, []);

  const loadMyRequests = async () => {
    try {
      const response = await axios.get(`${API}/athlete/join-requests`);
      setMyRequests(response.data);
    } catch (error) {
      console.error('Failed to load join requests');
    }
  };

  const searchTeams = async () => {
    if (!searchData.club_name && !searchData.team_name) {
      toast.error('Please enter a club name or team name to search');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchData.club_name) params.append('club_name', searchData.club_name);
      if (searchData.team_name) params.append('team_name', searchData.team_name);

      const response = await axios.get(`${API}/teams/search?${params}`);
      setSearchResults(response.data);
      setShowResults(true);
      
      if (response.data.length === 0) {
        toast.info('No teams found matching your search criteria');
      }
    } catch (error) {
      toast.error('Failed to search teams');
    } finally {
      setLoading(false);
    }
  };

  const submitJoinRequest = async (team) => {
    try {
      const response = await axios.post(`${API}/teams/join-request`, {
        club_name: team.club_name,
        team_name: team.name,
        message: joinMessage
      });

      toast.success(response.data.message);
      setSelectedTeam(null);
      setJoinMessage('');
      loadMyRequests();
      searchTeams(); // Refresh search results
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send join request');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const respondToInvite = async (inviteId, action) => {
    try {
      const response = await axios.post(`${API}/athlete/respond-invite/${inviteId}?action=${action}`);
      toast.success(response.data.message);
      loadMyRequests(); // Refresh the requests list
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} invitation`);
    }
  };

  return (
    <Card className="border-0 shadow-lg bg-blue-900/95 backdrop-blur-sm border border-blue-700">
      <CardHeader>
        <CardTitle className="text-white">Find & Join Teams</CardTitle>
        <CardDescription>Search for teams by club name and team name to request to join</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Search Form */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="club_search" className="text-white">Club Name</Label>
              <Input
                id="club_search"
                value={searchData.club_name}
                onChange={(e) => setSearchData({...searchData, club_name: e.target.value})}
                placeholder="e.g., Elite Soccer Academy"
                className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white placeholder:text-gray-400"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="team_search" className="text-white">Team Name</Label>
              <Input
                id="team_search"
                value={searchData.team_name}
                onChange={(e) => setSearchData({...searchData, team_name: e.target.value})}
                placeholder="e.g., Varsity Team"
                className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white placeholder:text-gray-400"
              />
            </div>
          </div>
          <Button 
            onClick={searchTeams} 
            disabled={loading}
            className="bg-yellow-500 hover:bg-emerald-600"
          >
            {loading ? 'Searching...' : 'Search Teams'}
          </Button>
        </div>

        {/* Search Results */}
        {showResults && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Search Results</h3>
            {searchResults.length === 0 ? (
              <p className="text-yellow-400">No teams found matching your search.</p>
            ) : (
              <div className="space-y-3">
                {searchResults.map((team) => (
                  <div key={team.id} className="p-4 border border-blue-700 rounded-lg bg-blue-900">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-semibold text-white">{team.club_name} - {team.name}</h4>
                        <p className="text-sm text-yellow-400">Sport: {team.sport} | Level: {team.level}</p>
                        <p className="text-sm text-yellow-400">Coach: {team.coach_name}</p>
                        <p className="text-sm text-yellow-400">Players: {team.players?.length || 0}</p>
                      </div>
                      <div className="ml-4">
                        {team.is_member ? (
                          <Badge className="bg-sky-900/90 text-sky-200 border border-sky-400">Already a member</Badge>
                        ) : team.has_pending_request ? (
                          <Badge className="bg-yellow-500/90 text-blue-900 border border-yellow-400">Request pending</Badge>
                        ) : (
                          <Button 
                            size="sm"
                            onClick={() => setSelectedTeam(team)}
                            className="bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-semibold"
                          >
                            Request to Join
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* My Join Requests */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">My Join Requests & Invitations</h3>
          {!myRequests || myRequests.length === 0 ? (
            <p className="text-yellow-400">No requests or invitations.</p>
          ) : (
            <div className="space-y-3">
              {myRequests.map((request) => (
                <div key={request.id} className={`p-4 border rounded-lg ${
                  request.type === 'incoming' 
                    ? 'border-green-500 bg-green-900/20' 
                    : 'border-blue-700 bg-blue-900/50'
                }`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      {request.type === 'incoming' ? (
                        <>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-1 bg-green-700 text-green-200 text-xs rounded">INVITATION</span>
                          </div>
                          <h4 className="font-semibold text-white">{request.club_name || 'Unknown Club'} - {request.team_name || 'Unknown Team'}</h4>
                          <p className="text-sm text-gray-300">Coach {request.coach_name || 'Unknown'} has invited you to join their team</p>
                          {request.message && (
                            <p className="text-sm text-gray-400 mt-1 italic">"{request.message}"</p>
                          )}
                          <div className="flex gap-2 mt-3">
                            <Button
                              onClick={() => respondToInvite(request.id, 'accept')}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              Accept Invitation
                            </Button>
                            <Button
                              onClick={() => respondToInvite(request.id, 'reject')}
                              size="sm"
                              variant="outline"
                              className="border-red-500 text-red-400 hover:bg-red-600 hover:text-white"
                            >
                              Decline
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-1 bg-blue-700 text-blue-200 text-xs rounded">YOUR REQUEST</span>
                          </div>
                          <h4 className="font-semibold text-white">{request.club_name || 'Unknown Club'} - {request.team_name || 'Unknown Team'}</h4>
                          <p className="text-sm text-gray-300">Coach: {request.coach_name || 'Unknown'}</p>
                          {request.message && (
                            <p className="text-sm text-gray-400 mt-1">Your message: "{request.message}"</p>
                          )}
                          <p className="text-sm text-yellow-400 mt-2">
                            Status: <span className="capitalize">{request.status || 'pending'}</span>
                          </p>
                        </>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        {request.created_at ? new Date(request.created_at).toLocaleDateString() : 'Date unknown'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Join Request Modal */}
        {selectedTeam && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Request to Join Team</CardTitle>
                <CardDescription>
                  {selectedTeam.club_name} - {selectedTeam.name}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Coach: {selectedTeam.coach_name}</Label>
                  <Label>Sport: {selectedTeam.sport} | Level: {selectedTeam.level}</Label>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="join_message">Message to Coach (Optional)</Label>
                  <Textarea
                    id="join_message"
                    value={joinMessage}
                    onChange={(e) => setJoinMessage(e.target.value)}
                    placeholder="Tell the coach why you'd like to join their team..."
                    rows={4}
                    className="border-blue-700 focus:border-yellow-500"
                  />
                </div>
                <div className="flex space-x-2">
                  <Button 
                    onClick={() => submitJoinRequest(selectedTeam)} 
                    className="flex-1 bg-yellow-500 hover:bg-emerald-600"
                  >
                    Send Request
                  </Button>
                  <Button 
                    onClick={() => {
                      setSelectedTeam(null);
                      setJoinMessage('');
                    }} 
                    variant="outline" 
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TeamJoinRequestCard;
